"use client";

import { createContext, useContext, useEffect, useState } from "react";
import { getJobPageAuthState, type JobPageAuthState } from "@/app/jobs/[id]/actions";

const DEFAULT_STATE: JobPageAuthState = { isAuthed: false, isSaved: false, isPaid: false };
const JobPageAuthContext = createContext<JobPageAuthState>(DEFAULT_STATE);

/**
 * Per-visitor auth/save/paid state for the job page. The page itself is
 * ISR-cached and identical for every visitor; this provider starts
 * signed-out-safe (matching the cached HTML exactly, so no hydration
 * mismatch) and reconciles from the server once mounted — same pattern as
 * HeaderCta's client-side session check.
 */
export function JobPageAuthProvider({
  jobId,
  children,
}: {
  jobId: number;
  children: React.ReactNode;
}) {
  const [state, setState] = useState<JobPageAuthState>(DEFAULT_STATE);

  useEffect(() => {
    let alive = true;
    getJobPageAuthState(jobId)
      .then((s) => {
        if (alive) setState(s);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [jobId]);

  return (
    <JobPageAuthContext.Provider value={state}>{children}</JobPageAuthContext.Provider>
  );
}

export function useJobPageAuth(): JobPageAuthState {
  return useContext(JobPageAuthContext);
}
